import React, { useEffect, useRef } from 'react';
import { Check } from 'lucide-react';

import {
  buildProvisionedMessage,
  buildSuccessMessage,
  SIGN_IN_HREF,
  SUCCESS_SIGN_IN_LABEL,
  SUCCESS_TITLE
} from './constants';
import type { RequestAccessSuccessContext } from './types';

import styles from './styles.module.css';

import { track } from '@/shared/analytics/track';

interface SuccessPanelProps {
  context: RequestAccessSuccessContext;
}

const SuccessPanel: React.FC<SuccessPanelProps> = ({ context }) => {
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Swapping the form out drops focus to <body>, so move it onto the heading.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className={styles.success} role="status">
      <span className={styles.check} aria-hidden="true">
        <Check size={28} strokeWidth={2} />
      </span>
      <h2 className={styles.title} ref={headingRef} tabIndex={-1}>
        {SUCCESS_TITLE}
      </h2>
      <p className={styles.subtitle}>
        {/* Auto-provisioned requests are already done; only queued ones wait on a human. */}
        {context.provisioned
          ? buildProvisionedMessage(
              context.companyName,
              context.colleagueEmails.length > 0
            )
          : buildSuccessMessage(
              context.companyName,
              context.colleagueEmails.length > 0
            )}
      </p>
      {/* A sign-in from here is a completed funnel: requested access, was
          auto-provisioned, and is going straight in. Worth separating from a
          sign-in click by someone who never submitted. */}
      <a
        className={styles.successCta}
        href={SIGN_IN_HREF}
        onClick={() =>
          track(
            'sign_in_clicked',
            {
              location: 'request_access_success',
              provisioned: context.provisioned
            },
            { beforeNavigation: true }
          )
        }
      >
        {SUCCESS_SIGN_IN_LABEL} &nbsp;&rarr;
      </a>
    </div>
  );
};

export default SuccessPanel;
